import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import { useNotifications } from '../contexts/NotificationContext';
import { PolicyFormModal } from '../components/PolicyFormModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ImportExportManager } from '../components/ImportExportManager';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  FileText,
  Calendar,
  User,
  Building2,
  Filter,
  Download,
  Upload,
  AlertTriangle,
  Calculator,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  XCircle,
  RefreshCw,
  Clock,
  History,
  Trophy,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Info,
  CreditCard,
  Ban
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIE_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

const RAMO_COLORS = {
  'Ahorro':    '#6366f1',
  'Inversión': '#22c55e',
  'Riesgo':    '#f59e0b',
  'Daños':     '#ef4444',
  'Otros':     '#94a3b8',
};

const RAMOS_FIJOS = ['Ahorro', 'Inversión', 'Riesgo', 'Daños', 'Otros'];

// Colores por aseguradora/línea de negocio
const businessLineColors = {
  allianz: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  qualitas: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  insignia: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  flipping: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
};

// Estados de póliza
const policyStatuses = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  pending_payment: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  expired: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  renewed:   'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  rehabilitated: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
  no_renewed: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
};

const statusLabels = {
  active: 'Activa',
  pending: 'Pendiente',
  pending_payment: 'Pendiente de Pago',
  expired: 'Vencida',
  cancelled: 'Cancelada',
  renewed:   'Renovada',
  rehabilitated: 'Rehabilitada',
  no_renewed: 'No renovó'
};

const movementTypeLabels = {
  emision: 'Emisión',
  cancelacion: 'Cancelación',
  rehabilitacion: 'Rehabilitación'
};

const intentionLabels = {
  renovar: 'Renovar',
  cancelar: 'Cancelar',
  no_definido: 'No definido'
};

const DAYS_OVERDUE_COLOR_CLASS = {
  green:   'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  yellow:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  orange:  'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  red:     'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const SEDE_COLORS_POL = {
  'Guadalajara':      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'Querétaro':        'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'Ciudad de México': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

// Definición de columnas para personalización
const defaultPolicyColumns = [
  { id: 'policy_number', label: 'Número Póliza', visible: true },
  { id: 'client', label: 'Cliente', visible: true },
  { id: 'agent', label: 'Agente', visible: true },
  { id: 'business_line', label: 'Aseguradora', visible: true },
  { id: 'sede', label: 'Sede', visible: true },
  { id: 'product_type', label: 'Producto', visible: true },
  { id: 'premium', label: 'Prima/Aportaciones', visible: true },
  { id: 'vigencia', label: 'Vigencia', visible: true },
  { id: 'movement_type', label: 'Tipo movimiento', visible: true },
  { id: 'status', label: 'Estado', visible: true },
  { id: 'days_overdue', label: 'Días retraso', visible: true },
  { id: 'client_intention', label: 'Intención cliente', visible: false },
  { id: 'convention_pts', label: 'Conv.', visible: true },
  { id: 'chargeback', label: 'Chargeback', visible: true },
  { id: 'actions', label: 'Acciones', visible: true }
];

// Productos de ahorro OptiMaxx sujetos a 18 aportaciones de chargeback
const isOPlusProduct = (productType) => {
  if (!productType) return false;
  const p = productType.toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[\s.]/g, "");
  return (
    p.includes("optimaxx") ||
    p.includes("optimax") ||
    p.includes("oplus") ||
    p.includes("oplusart") ||
    p.includes("plus")
  );
};

// ── Convención Allianz 2026 ───────────────────────────────────────────────────
const CONV_VIP      = 120;
const CONV_ESTELAR  = 180;
// Nombres reales confirmados con SELECT DISTINCT desde BD (2026-05-13)
const CONV_AHORRO_PRODUCTS      = new Set(['OptiMaxx Plus', 'O. Plus', 'O.Plus', 'Ahorro Educación', 'Ahorro', 'Optimaxx Plus', 'Optimaxx Educacional']);
const CONV_PROTECCION_PRODUCTS  = new Set(['Vida Individual', 'OPTIMAXX EDUCACIONAL INVALIDEZ', 'Optimaxx Protección']);
const CONV_GMMI_PRODUCTS        = new Set(['Gastos Médicos Mayores', 'Vida Grupo / GMM Empresarial', 'Gastos Médicos Mayores Individual']);
const CONV_AUTOS_PRODUCTS       = new Set(['Auto Allianz', 'Auto']);
const CONV_RESIDENCIAL_PRODUCTS = new Set(['Hogar / Residencial Premier', 'Residencial Pesos', 'Residencial Dólares']);
const CONV_INVERSION_PRODUCTS   = new Set(['Inversión Elite', 'Inversión Patrimonial', 'Rentas Privadas', 'OptiMaxx Elite', 'OptiMaxx Patrimonial', 'Optimaxx Elite', 'Optimaxx Patrimonial']);

// Puntos que aporta UNA póliza individualmente (para la columna Conv.)
// Retorna null si no es Allianz, { nonQualifying: true } si no califica (año ≠ 2026, cancelada, etc.)
function calculatePolicyPoints(policy) {
  const bl = (policy.business_line || '').toLowerCase();
  if (bl !== 'allianz') {
    if (bl === 'insignia') return { pts: null, label: 'Insignia Life', prima: policy.premium || 0, pendiente: true };
    if (bl === 'qualitas') return { pts: null, label: 'Quálitas',     prima: policy.premium || 0, pendiente: true };
    return null;
  }

  // Excluye canceladas y no renovadas; rehabilitadas cuentan igual que active/pending_payment
  // (mismo criterio que calculate_convention_points() del backend)
  if (!['active', 'pending_payment', 'rehabilitated'].includes(policy.status)) {
    return { nonQualifying: true };
  }
  if ((policy.movement_type || '').toLowerCase() === 'renovacion') {
    return { nonQualifying: true };
  }

  const pt = policy.product_type || '';
  const premium       = policy.premium       || 0;
  // Fallback: usar valor_plan_original cuando contributions=0 (pólizas importadas pre-fix)
  // policy.valor_plan_original es campo plano expuesto por _enrich_policy(); el lookup
  // en custom_values es respaldo para cualquier ruta que no pase por _enrich_policy.
  const vpo = policy.valor_plan_original
    || parseFloat((policy.custom_values || {})['valor_plan_original']) || 0;
  const contributions = policy.contributions > 0 ? policy.contributions : vpo;

  if (CONV_AHORRO_PRODUCTS.has(pt)) {
    const pts = contributions >= 1_800_000 ? 2.0
              : contributions >= 900_000   ? 1.0
              : contributions > 0          ? 0.5 : 0;
    const basis = contributions === vpo && vpo > 0
      ? `valor plan: $${contributions.toLocaleString('es-MX')}`
      : `contribuciones: $${contributions.toLocaleString('es-MX')}`;
    return { pts, label: 'Ahorro', basis };
  }
  if (CONV_PROTECCION_PRODUCTS.has(pt)) {
    const pts = premium >= 15_000 ? 4.0
              : premium >= 10_000 ? 3.0
              : premium >= 5_000  ? 1.5 : 0;
    return { pts, label: 'Protección', basis: `prima: $${premium.toLocaleString('es-MX')}` };
  }
  if (CONV_GMMI_PRODUCTS.has(pt)) {
    const pts = premium >= 30_000 ? 3.0
              : premium >= 20_000 ? 2.0
              : premium >= 10_000 ? 1.0 : 0;
    return { pts, label: 'GMMI', basis: `prima: $${premium.toLocaleString('es-MX')}` };
  }
  if (CONV_AUTOS_PRODUCTS.has(pt)) {
    const pts = premium >= 15_000 ? 1.5
              : premium >= 10_000 ? 1.0
              : premium >= 5_000  ? 0.5 : 0;
    return { pts, label: 'Autos (tope 120 cat.)', basis: `prima: $${premium.toLocaleString('es-MX')}` };
  }
  if (CONV_RESIDENCIAL_PRODUCTS.has(pt)) {
    const pts = premium >= 15_000 ? 1.5
              : premium >= 10_000 ? 1.0
              : premium >= 5_000  ? 0.5 : 0;
    return { pts, label: 'Residencial', basis: `prima: $${premium.toLocaleString('es-MX')}` };
  }
  if (CONV_INVERSION_PRODUCTS.has(pt)) {
    if (['Inversión Elite', 'Rentas Privadas', 'OptiMaxx Elite', 'Optimaxx Elite'].includes(pt)) {
      if (contributions >= 2_500_000) {
        const pts = Math.min(2.5 * (contributions / 1_000_000), 25);
        return { pts: Math.round(pts * 100) / 100, label: 'Inversión Elite', basis: `contribuciones: $${contributions.toLocaleString('es-MX')}`, note: 'tope 25 pts/cliente' };
      }
      return { pts: 0, label: 'Inversión Elite', basis: `< 2.5M (sin puntos)` };
    }
    if (['Inversión Patrimonial', 'OptiMaxx Patrimonial', 'Optimaxx Patrimonial'].includes(pt)) {
      const pts = Math.min(Math.floor(contributions / 250_000), 25);
      return { pts, label: 'Inversión Patrimonial', basis: `contribuciones: $${contributions.toLocaleString('es-MX')}`, note: 'tope 25 pts/cliente' };
    }
  }
  return { nonQualifying: true }; // producto Allianz no categorizado
}

// Calcular puntos totales de convención desde la lista de pólizas activas 2026
function computeConventionPoints(policies) {
  const active2026 = policies.filter(p => {
    if (p.status !== 'active') return false;
    const d = p.created_at ? new Date(p.created_at) : null;
    return d && d.getFullYear() === 2026;
  });

  const breakdown = { ahorro: 0, proteccion: 0, gmmi: 0, autos: 0, residencial: 0, inversion: 0 };
  let autosRaw = 0;
  let primaInsignia = 0;
  let primaQualitas = 0;
  const inversionByClient = {};

  for (const p of active2026) {
    const bl = (p.business_line || '').toLowerCase();
    const pt = p.product_type || '';
    const premium       = p.premium       || 0;
    const contributions = p.contributions || 0;
    const clientKey     = p.client_id || p.client_name || 'unknown';

    if (bl === 'allianz') {
      if (CONV_AHORRO_PRODUCTS.has(pt)) {
        breakdown.ahorro += contributions >= 1_800_000 ? 2.0 : contributions >= 900_000 ? 1.0 : contributions > 0 ? 0.5 : 0;
      } else if (CONV_PROTECCION_PRODUCTS.has(pt)) {
        breakdown.proteccion += premium >= 15_000 ? 4.0 : premium >= 10_000 ? 3.0 : premium >= 5_000 ? 1.5 : 0;
      } else if (CONV_GMMI_PRODUCTS.has(pt)) {
        breakdown.gmmi += premium >= 30_000 ? 3.0 : premium >= 20_000 ? 2.0 : premium >= 10_000 ? 1.0 : 0;
      } else if (CONV_AUTOS_PRODUCTS.has(pt)) {
        autosRaw += premium >= 15_000 ? 1.5 : premium >= 10_000 ? 1.0 : premium >= 5_000 ? 0.5 : 0;
      } else if (CONV_RESIDENCIAL_PRODUCTS.has(pt)) {
        breakdown.residencial += premium >= 15_000 ? 1.5 : premium >= 10_000 ? 1.0 : premium >= 5_000 ? 0.5 : 0;
      } else if (CONV_INVERSION_PRODUCTS.has(pt)) {
        const cur = inversionByClient[clientKey] || 0;
        let add = 0;
        if (pt === 'Inversión Elite' || pt === 'Rentas Privadas') {
          if (contributions >= 2_500_000) add = Math.min(2.5 * (contributions / 1_000_000), 25 - cur);
        } else if (pt === 'Inversión Patrimonial') {
          add = Math.min(Math.floor(contributions / 250_000), 25 - cur);
        }
        inversionByClient[clientKey] = cur + Math.max(add, 0);
      }
    } else if (bl === 'insignia') {
      primaInsignia += premium;
    } else if (bl === 'qualitas') {
      primaQualitas += premium;
    }
  }

  breakdown.autos    = Math.min(autosRaw, 120);
  breakdown.inversion= Object.values(inversionByClient).reduce((s, v) => s + v, 0);

  const total = Object.values(breakdown).reduce((s, v) => s + v, 0);
  const rounded = v => Math.round(v * 100) / 100;

  return {
    total_points:      rounded(total),
    vip_threshold:     CONV_VIP,
    estelar_threshold: CONV_ESTELAR,
    vip_qualified:     total >= CONV_VIP,
    estelar_qualified: total >= CONV_ESTELAR,
    breakdown: {
      ahorro:     rounded(breakdown.ahorro),
      proteccion: rounded(breakdown.proteccion),
      gmmi:       rounded(breakdown.gmmi),
      autos:      rounded(breakdown.autos),
      residencial:rounded(breakdown.residencial),
      inversion:  rounded(breakdown.inversion),
    },
    prima_insignia: rounded(primaInsignia),
    prima_qualitas: rounded(primaQualitas),
    policy_count: active2026.length,
  };
}

// Función para calcular días sin pago
const getDaysSinceStart = (startDate) => {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const now = new Date();
  const diffTime = Math.abs(now - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const _PERIODOS_MESES = { mensual: 1, bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };
const _PERIODOS_DIAS  = { quincenal: 15, semanal: 7 };
function previewSerieRecibo(serieStr, n) {
  if (!serieStr || !n || isNaN(n) || n <= 0) return null;
  const parts = serieStr.split('/');
  if (parts.length !== 2) return null;
  const x = parseInt(parts[0], 10);
  return isNaN(x) ? null : `${x + n}/${parts[1]}`;
}
function previewFechaRecibo(fechaStr, n, periodicidad) {
  if (!fechaStr || !n || isNaN(n) || n <= 0) return null;
  const p = (periodicidad || 'mensual').toLowerCase();
  const parts = fechaStr.split('/');
  if (parts.length !== 3) return null;
  const dt = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  if (isNaN(dt.getTime())) return null;
  const dias = _PERIODOS_DIAS[p];
  if (dias) { dt.setDate(dt.getDate() + dias * n); }
  else { dt.setMonth(dt.getMonth() + (_PERIODOS_MESES[p] || 1) * n); }
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}
function fmtMoney(n) {
  return n == null ? '—' : Number(n).toLocaleString('es-MX');
}

export const PoliciesPage = () => {
  const { user, canCreate, canEdit, canDelete, isAdmin, isMaster, isAsistente, isDeveloper } = useAuth();
  const { selectedLine, isAllLines, filterByBusinessLine, availableLines } = useBusinessLine();
  const { generateReminders } = useNotifications();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [policies, setPolicies] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [movementFilter, setMovementFilter] = useState('all');
  const [productoFilter, setProductoFilter] = useState(searchParams.get('producto') || '');
  const [distributionData, setDistributionData] = useState([]);
  const [ramosData, setRamosData] = useState({});
  const [ramoSeleccionado, setRamoSeleccionado] = useState(null);
  const [sortBy, setSortBy] = useState('client_name');
  const [sortDir, setSortDir] = useState('asc');
  // Tabs
  const getDefaultTab = () => {
    if (isAllLines) return 'all';
    if (selectedLine === 'insignia') return 'insignia';
    if (selectedLine === 'qualitas') return 'qualitas';
    return 'allianz';
  };
  const [activeAseguradora, setActiveAseguradora] = useState(getDefaultTab);
  const [activeGrupo, setActiveGrupo] = useState('todas');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalAcumulado, setTotalAcumulado] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [renewedCount,    setRenewedCount]    = useState(0);
  const [rehabilitatedCount, setRehabilitatedCount] = useState(0);
  const [inversionCount,        setInversionCount]        = useState(0);
  const [omittedCount,          setOmittedCount]          = useState(0);
  const [pendingRenewalCount,   setPendingRenewalCount]   = useState(0);
  const [renovadasActivasCount, setRenovadasActivasCount] = useState(0);
  const [noRenovadasCount,      setNoRenovadasCount]      = useState(0);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [columns, setColumns] = useState(defaultPolicyColumns);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelDate, setCancelDate] = useState('');
  const [rehabilitateDialogOpen, setRehabilitateDialogOpen] = useState(false);
  const [rehabilitateTarget, setRehabilitateTarget] = useState(null);
  const [rehabilitateDate, setRehabilitateDate] = useState('');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyPolicy, setHistoryPolicy] = useState(null);
  const [convPoints, setConvPoints] = useState(null);
  const [convPanelOpen, setConvPanelOpen] = useState(true);
  const [convAgentId, setConvAgentId] = useState(null);
  const [policyCustomFields, setPolicyCustomFields] = useState([]);
  const [chargebackModal, setChargebackModal] = useState(null);
  const [actualizarPagosModal, setActualizarPagosModal] = useState(null);
  const [actualizarPagosInput, setActualizarPagosInput] = useState('');
  const [actualizarPagosDate, setActualizarPagosDate] = useState('');
  const [actualizarPagosLoading, setActualizarPagosLoading] = useState(false);
  const [adelantadasActivo, setAdelantadasActivo] = useState(false);
  const [adelantadasTipo, setAdelantadasTipo] = useState('cantidad');
  const [adelantadasCantidad, setAdelantadasCantidad] = useState('');
  const [adelantadasMonto, setAdelantadasMonto] = useState('');
  const [suspenderPagos, setSuspenderPagos] = useState(null);
  const [modoIngreso, setModoIngreso] = useState('total');
  const [aportacionesIncrementales, setAportacionesIncrementales] = useState('');
  const [agentFilter, setAgentFilter] = useState(user?.id);
  const [statusFilter, setStatusFilter] = useState('all');
  const [emisorFilter, setEmisorFilter] = useState('all');
  const [retrasoFilter, setRetrasoFilter] = useState('all');
  const [startDateFrom, setStartDateFrom] = useState('');
  const [startDateTo, setStartDateTo] = useState('');
  const [emisorOptions, setEmisorOptions] = useState([]);

  // Debounce search input 400ms
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Sync tab with global business line selector
  useEffect(() => {
    if (isAllLines) {
      setActiveAseguradora('all');
    } else {
      setActiveAseguradora(selectedLine);
    }
  }, [selectedLine, isAllLines]);

  // Cargar usuarios gestionados para el Asistente
  useEffect(() => {
    if (isAsistente) {
      const managed = user?.managed_agents || [];
      if (managed.length > 0) {
        axios.get(`${API}/users`)
          .then(r => setManagedUsers(r.data.filter(u => managed.includes(u.id))))
          .catch(() => {});
      }
    }
  }, [isAsistente]);

  // Fetch when paginated params change
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAseguradora, activeGrupo, currentPage, searchDebounced, movementFilter, productoFilter, sortBy, sortDir, startYear, agentFilter, statusFilter, emisorFilter, startDateFrom, startDateTo]);

  // Fetch available years when aseguradora changes
  useEffect(() => {
    const yearsParams = new URLSearchParams();
    if (activeAseguradora !== 'all') yearsParams.append('business_line', activeAseguradora);
    axios.get(`${API}/policies/available-start-years?${yearsParams.toString()}`)
      .then(r => setAvailableYears(r.data || []))
      .catch(() => setAvailableYears([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAseguradora]);

  // Fetch distribution when aseguradora, grupo, or agentFilter changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeAseguradora !== 'all') params.append('aseguradora', activeAseguradora);
    if (activeGrupo !== 'todas') params.append('grupo', activeGrupo);
    if (agentFilter && agentFilter !== 'all' && (isAdmin || isMaster || isAsistente || isDeveloper)) {
      params.append('agent_id', agentFilter);
    }
    axios.get(`${API}/policies/distribution?${params.toString()}`)
      .then(r => {
        setDistributionData(r.data.items || []);
        setRamosData(r.data.ramos || {});
        setRamoSeleccionado(null);
      })
      .catch(() => { setDistributionData([]); setRamosData({}); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAseguradora, activeGrupo, agentFilter]);

  // Load unique emisor options when aseguradora tab changes
  useEffect(() => {
    const emisorParams = new URLSearchParams();
    if (activeAseguradora !== 'all') emisorParams.append('business_line', activeAseguradora);
    axios.get(`${API}/policies/emisores?${emisorParams.toString()}`)
      .then(r => setEmisorOptions(r.data || []))
      .catch(() => setEmisorOptions([]));
    setEmisorFilter('all');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAseguradora]);

  // Fetch convention points when on Allianz or Insignia Life tab
  useEffect(() => {
    if (!['allianz', 'insignia'].includes(activeAseguradora)) { setConvPoints(null); return; }
    const agentId = convAgentId || user?.id;
    if (!agentId) { setConvPoints(null); return; }
    axios.get(`${API}/agents/${agentId}/convention-points`)
      .then(r => setConvPoints(r.data))
      .catch(() => setConvPoints(null));
  }, [activeAseguradora, user, convAgentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        page_size: 20,
      });
      if (activeAseguradora !== 'all') params.append('business_line', activeAseguradora);
      if (activeGrupo !== 'todas') params.append('grupo', activeGrupo);
      if (productoFilter) params.append('producto', productoFilter);
      if (searchDebounced) params.append('search', searchDebounced);
      if (movementFilter !== 'all') params.append('movement_type', movementFilter);
      if (startYear !== null) params.append('start_year', startYear);
      if (agentFilter && agentFilter !== 'all' && (isAdmin || isMaster || isAsistente || isDeveloper)) params.append('agent_id', agentFilter);
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (emisorFilter !== 'all') params.append('emisor_filter', emisorFilter);
      if (startDateFrom) params.append('start_date_from', startDateFrom);
      if (startDateTo) params.append('start_date_to', startDateTo);
      params.append('sort_by', sortBy);
      params.append('sort_dir', sortDir);

      const [policiesRes, clientsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/policies?${params.toString()}`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/agents`)
      ]);

      const data = policiesRes.data;
      setPolicies(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
      setTotalAcumulado(data.total_acumulado ?? 0);
      setActiveCount(data.active_count ?? 0);
      setPendingCount(data.pending_count ?? 0);
      setCancelledCount(data.cancelled_count ?? 0);
      setRenewedCount(data.renewed_count ?? 0);
      setRehabilitatedCount(data.rehabilitated_count ?? 0);
      setInversionCount(data.inversion_count ?? 0);
      setOmittedCount(data.omitted_count ?? 0);
      setPendingRenewalCount(data.pending_renewal_count ?? 0);
      setRenovadasActivasCount(data.renovadas_activas_count ?? 0);
      setNoRenovadasCount(data.no_renovadas_count ?? 0);

      setClients(clientsRes.data || []);
      const mappedAgents = (agentsRes.data || []).map(a => ({
        id: a.user?.id || a.user_id,
        full_name: a.user?.full_name || 'Sin nombre',
        email: a.user?.email || ''
      }));
      if (isDeveloper && user && !mappedAgents.find(a => a.id === user.id)) {
        mappedAgents.unshift({ id: user.id, full_name: user.full_name || 'Yo', email: user.email || '' });
      }
      setAgents(mappedAgents);
      try {
        const cfRes = await axios.get(`${API}/policy-custom-fields`);
        setPolicyCustomFields(cfRes.data || []);
      } catch { /* silencioso */ }
    } catch (error) {
      console.error('Error fetching policies data:', error);
      setPolicies([]);
      toast.error('Error al cargar datos de pólizas');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPoliciesForExport = async () => {
    const params = new URLSearchParams();
    if (activeAseguradora !== 'all') params.append('business_line', activeAseguradora);
    if (activeGrupo !== 'todas') params.append('grupo', activeGrupo);
    if (productoFilter) params.append('producto', productoFilter);
    if (searchDebounced) params.append('search', searchDebounced);
    if (movementFilter !== 'all') params.append('movement_type', movementFilter);
    if (startYear !== null) params.append('start_year', startYear);
    if (agentFilter && agentFilter !== 'all' && (isAdmin || isMaster || isAsistente || isDeveloper)) params.append('agent_id', agentFilter);
    if (emisorFilter !== 'all') params.append('emisor_filter', emisorFilter);
    if (statusFilter !== 'all') params.append('status_filter', statusFilter);
    if (startDateFrom) params.append('start_date_from', startDateFrom);
    if (startDateTo) params.append('start_date_to', startDateTo);
    // Sin page → legacy flat list (todas las pólizas sin paginación)
    const res = await axios.get(`${API}/policies?${params.toString()}`);
    const allPolicies = Array.isArray(res.data) ? res.data : (res.data.items || []);

    // Pólizas del año anterior para delta-Daños (solo Allianz, año completo — no hereda startDate filters)
    let prevPolicies = [];
    if (activeAseguradora === 'allianz') {
      const prevYear = new Date().getFullYear() - 1;
      const prevParams = new URLSearchParams({ business_line: 'allianz' });
      prevParams.append('start_date_from', `${prevYear}-01-01`);
      prevParams.append('start_date_to', `${prevYear}-12-31`);
      if (agentFilter && agentFilter !== 'all' && (isAdmin || isMaster || isAsistente || isDeveloper))
        prevParams.append('agent_id', agentFilter);
      const resPrev = await axios.get(`${API}/policies?${prevParams.toString()}`);
      prevPolicies = Array.isArray(resPrev.data) ? resPrev.data : (resPrev.data.items || []);
    }

    // Lookup de custom fields por nombre → id
    const fieldById = {};
    (policyCustomFields || []).forEach(f => { fieldById[f.name] = f.id; });
    const getCV = (p, name) => {
      const id = fieldById[name];
      return id ? ((p.custom_values || {})[id] ?? '') : '';
    };

    const exportRows = allPolicies.map(p => ({
      policy_number:          p.policy_number ?? '',
      client_name:            p.client_name ?? '',
      agent_name:             p.agent_name ?? '',
      business_line:          p.business_line ?? '',
      product_type:           p.product_type ?? '',
      status:                 p.status ?? '',
      emisor:                 getCV(p, 'emisor'),
      start_date:             p.start_date ?? '',
      end_date:               p.end_date ?? '',
      premium:                p.premium ?? '',
      prima_anual_reportada:  p.prima_anual_reportada ?? '',
      periodicidad:           getCV(p, 'periodicidad'),
      serie_recibo:           getCV(p, 'serie_recibo'),
      plazo_comprometido:     getCV(p, 'plazo_comprometido'),
      contributions:          p.contributions ?? '',
      aportaciones_esperadas: getCV(p, 'aportaciones_esperadas'),
      valor_plan_original:    p.valor_plan_original ?? getCV(p, 'valor_plan_original'),
      fecha_recibo_pendiente: p.fecha_recibo_pendiente ?? getCV(p, 'fecha_recibo_pendiente'),
      dias_retraso:           p.dias_retraso ?? '',
      plan_contratado:        getCV(p, 'plan_contratado'),
      conducto_cobro:         getCV(p, 'conducto_cobro'),
      respuesta_banco:        getCV(p, 'respuesta_banco'),
      sede:                   p.sede ?? '',
      clave_agente:           p.clave_agente ?? '',
      conv_points:            (() => { const cp = calculatePolicyPoints(p); return (cp && !cp.nonQualifying && cp.pts != null) ? cp.pts : ''; })(),
    }));

    // Sección resumen delta-Daños al final del export (solo Allianz)
    if (activeAseguradora === 'allianz' && prevPolicies.length > 0) {
      const DANOS_SET = new Set(['Auto', 'Residencial Pesos', 'Residencial Dólares']);
      const DB_CLIENT_CAP = 20.0;
      const DB_RAMO_CAP = 30.0;
      const prevYear = new Date().getFullYear() - 1;

      const curr26 = {}, prev25 = {};
      for (const p of allPolicies) {
        if (!DANOS_SET.has(p.product_type) || !['active', 'pending_payment'].includes(p.status)) continue;
        const k = (p.client_name || '').trim().toUpperCase();
        curr26[k] = (curr26[k] || 0) + (parseFloat(p.premium) || 0);
      }
      for (const p of prevPolicies) {
        if (!DANOS_SET.has(p.product_type)) continue;
        const k = (p.client_name || '').trim().toUpperCase();
        prev25[k] = (prev25[k] || 0) + (parseFloat(p.premium) || 0);
      }

      let totalDanosPts = 0;
      const clientRows = Object.entries(curr26)
        .map(([k, curr]) => {
          const prev = prev25[k] || 0;
          const delta = curr - prev;
          if (delta <= 0) return null;
          const pts_raw = Math.min((delta / 1_000_000) * 5, DB_CLIENT_CAP);
          const pts_eff = Math.min(pts_raw, Math.max(DB_RAMO_CAP - totalDanosPts, 0));
          totalDanosPts += pts_eff;
          return { k, prev, curr, delta, pts_eff };
        })
        .filter(Boolean);

      const fmt = n => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      exportRows.push(
        { policy_number: '' },
        { policy_number: `RESUMEN — DELTA DAÑOS ${prevYear}→${prevYear + 1} (Autos + Residencial vs año anterior)` },
        { policy_number: 'Cliente', client_name: `Prima ${prevYear}`, agent_name: `Prima ${prevYear + 1}`, business_line: 'Delta', product_type: 'Pts Conv.' },
        ...clientRows.map(r => ({
          policy_number: r.k,
          client_name:   `$${fmt(r.prev)}`,
          agent_name:    `$${fmt(r.curr)}`,
          business_line: `$${fmt(r.delta)}`,
          product_type:  `${r.pts_eff.toFixed(1)} pts`,
        })),
        { policy_number: 'TOTAL Daños', product_type: `${totalDanosPts.toFixed(1)} pts / ${DB_RAMO_CAP} pts cap` },
        { policy_number: 'Nota: los Puntos Conv. por póliza (columna anterior) no incluyen este componente. Para el total oficial de convención, consultar el Dashboard.' },
      );
    }

    return exportRows;
  };

  const handleTabAseguradora = (bl) => {
    setActiveAseguradora(bl);
    setActiveGrupo('todas');
    setProductoFilter('');
    setCurrentPage(1);
  };

  const handleTabGrupo = (g) => {
    setActiveGrupo(g);
    setCurrentPage(1);
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setDialogOpen(true);
  };

  const handleCancelPolicy = async () => {
    if (!cancelTarget) return;
    try {
      await axios.post(`${API}/policies/${cancelTarget.id}/cancel`, { cancelled_at: cancelDate });
      toast.success(`Póliza ${cancelTarget.policy_number} cancelada`);
      setCancelDialogOpen(false);
      setCancelTarget(null);
      setCancelDate('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar la póliza');
    }
  };

  const [reconcilingRenewals, setReconcilingRenewals] = useState(false);
  const handleReconcileRenewals = async () => {
    setReconcilingRenewals(true);
    try {
      const res = await axios.post(`${API}/policies/reconcile-renewals`);
      const { reconciled, still_pending } = res.data;
      if (reconciled > 0) {
        toast.success(`${reconciled} renovación(es) consolidada(s)${still_pending > 0 ? ` · ${still_pending} pendiente(s)` : ''}`);
        fetchData();
      } else {
        toast.info(still_pending > 0 ? `${still_pending} renovación(es) pendiente(s) sin candidato previo` : 'No hay renovaciones pendientes de consolidar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reconciliar renovaciones');
    } finally {
      setReconcilingRenewals(false);
    }
  };

  const handleMarkOmitted = async (policy) => {
    try {
      await axios.post(`${API}/policies/mark-omitted`, { policy_ids: [policy.id], source: 'manual' });
      toast.success(`Póliza ${policy.policy_number} marcada como omitida`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar la póliza como omitida');
    }
  };

  const handleRehabilitatePolicy = async () => {
    if (!rehabilitateTarget) return;
    try {
      await axios.post(`${API}/policies/${rehabilitateTarget.id}/rehabilitate`, { rehabilitated_at: rehabilitateDate });
      toast.success(`Póliza ${rehabilitateTarget.policy_number} rehabilitada`);
      setRehabilitateDialogOpen(false);
      setRehabilitateTarget(null);
      setRehabilitateDate('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rehabilitar la póliza');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta póliza?')) return;
    try {
      await axios.delete(`${API}/policies/${id}`);
      toast.success('Póliza eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar la póliza');
    }
  };

  const handleConfirmPago = async (policy) => {
    try {
      const res = await axios.patch(`${API}/policies/${policy.id}/aportacion`, { realizo_aportacion: true });
      const nuevas = res.data.numero_aportacion_actual;
      toast.success(`Pago registrado. Aportaciones: ${nuevas}/${policy.chargeback_serie_max ?? 18}`);
      setChargebackModal(null);
      fetchData();
    } catch (err) {
      toast.error('Error al registrar el pago');
    }
  };

  const closeActualizarPagos = () => {
    setActualizarPagosModal(null);
    setActualizarPagosInput('');
    setActualizarPagosDate('');
    setAdelantadasActivo(false);
    setAdelantadasTipo('cantidad');
    setAdelantadasCantidad('');
    setAdelantadasMonto('');
    setSuspenderPagos(null);
    setModoIngreso('total');
    setAportacionesIncrementales('');
  };

  const handleActualizarPagos = async () => {
    if (!actualizarPagosModal) return;
    let contributionsPaid;
    if (modoIngreso === 'incremental') {
      const n = parseInt(aportacionesIncrementales, 10);
      if (isNaN(n) || n <= 0) {
        toast.error('Ingresa un número válido de aportaciones a agregar');
        return;
      }
    } else {
      contributionsPaid = parseInt(actualizarPagosInput, 10);
      if (isNaN(contributionsPaid) || contributionsPaid < 0) {
        toast.error('Ingresa un número válido de aportaciones');
        return;
      }
    }
    setActualizarPagosLoading(true);
    try {
      const body = modoIngreso === 'incremental'
        ? { aportaciones_incrementales: parseInt(aportacionesIncrementales, 10) }
        : { contributions_paid: contributionsPaid };
      if (actualizarPagosDate) body.last_payment_date = actualizarPagosDate;
      if (adelantadasActivo) {
        if (adelantadasTipo === 'cantidad' && adelantadasCantidad) {
          const n = parseInt(adelantadasCantidad, 10);
          if (!isNaN(n) && n > 0) body.aportaciones_adelantadas = n;
        } else if (adelantadasTipo === 'monto' && adelantadasMonto) {
          const m = parseFloat(adelantadasMonto);
          if (!isNaN(m) && m > 0) body.monto_adelantado = m;
        }
        body.suspender_pagos = adelantadasTipo === 'monto' ? false : suspenderPagos;
      }
      const res = await axios.put(`${API}/policies/${actualizarPagosModal.id}/actualizar-pagos`, body);
      const cvUpdates = {};
      if (res.data.serie_recibo != null)
        cvUpdates['9e4ab091-54de-4980-9eed-e6af30fbb3bf'] = res.data.serie_recibo;
      if (res.data.fecha_recibo_pendiente != null)
        cvUpdates['6f0d045f-0329-4d21-898e-f97f299e475a'] = res.data.fecha_recibo_pendiente;
      if (res.data.valor_plan_acumulado != null)
        cvUpdates['569aa9ab-8dd6-4e5b-94e8-0c4b32c45042'] = String(res.data.valor_plan_acumulado);
      const ufpField = policyCustomFields.find(f => f.name === 'ultima_fecha_pago');
      if (ufpField && res.data.last_payment_date)
        cvUpdates[ufpField.id] = res.data.last_payment_date;
      setPolicies(prev => prev.map(p =>
        p.id === actualizarPagosModal.id
          ? {
              ...p,
              numero_aportacion_actual: res.data.contributions_paid,
              status: res.data.dias_retraso === 0 && p.status === 'pending_payment' ? 'active' : p.status,
              dias_retraso: res.data.dias_retraso,
              dias_retraso_calculado: res.data.dias_retraso,
              dias_retraso_display: res.data.estado_display,
              dias_retraso_color: res.data.dias_retraso === 0 ? 'green' : 'red',
              chargeback_status: res.data.chargeback_aplica === false
                ? 'no_aplica'
                : (res.data.dias_retraso === 0 ? 'al_corriente' : 'pendiente'),
              chargeback_badge: res.data.chargeback_aplica === false
                ? 'gris'
                : (res.data.dias_retraso === 0 ? 'verde' : 'rojo'),
              ...(Object.keys(cvUpdates).length > 0 && {
                custom_values: { ...p.custom_values, ...cvUpdates }
              }),
              ...(res.data.fecha_recibo_pendiente != null && {
                fecha_recibo_pendiente: res.data.fecha_recibo_pendiente
              }),
            }
          : p
      ));
      toast.success(`Pagos actualizados — ${res.data.contributions_paid} aportaciones — ${res.data.estado_display}`);
      closeActualizarPagos();
    } catch (error) {
      const errorMsg = Array.isArray(error.response?.data?.detail)
        ? error.response.data.detail.map(e => e.msg).join(', ')
        : (error.response?.data?.detail || 'Error al actualizar pagos');
      toast.error(errorMsg);
    } finally {
      setActualizarPagosLoading(false);
    }
  };


  // Renderizar columna Chargeback usando chargeback_status / chargeback_badge del backend
  const renderChargeback = (policy) => {
    const cbStatus = policy.chargeback_status;
    const cbBadge  = policy.chargeback_badge;

    // Sin chargeback aplicable: badge estático
    if (cbStatus === 'no_aplica') {
      return (
        <Badge className="bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          No aplica
        </Badge>
      );
    }

    // Al corriente: badge estático verde
    if (cbBadge === 'verde' || cbStatus === 'al_corriente') {
      const aportaciones = policy.serie_recibo_numerador ?? policy.numero_aportacion_actual ?? 0;
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
          Al corriente ({aportaciones}/{policy.chargeback_serie_max ?? 18})
        </Badge>
      );
    }

    // Todos los estados con riesgo de chargeback: botón "Revisar" con color dinámico
    const colorClass =
      cbBadge === 'rojo'    ? 'bg-red-500 hover:bg-red-600' :
      cbBadge === 'naranja' ? 'bg-orange-500 hover:bg-orange-600' :
      'bg-yellow-500 hover:bg-yellow-600'; // amarillo (default)

    return (
      <Button
        size="sm"
        className={`text-xs font-semibold text-white animate-pulse flex items-center gap-1 ${colorClass}`}
        onClick={() => setChargebackModal({ policy })}
      >
        <AlertTriangle className="h-3 w-3" />
        Revisar
      </Button>
    );
  };

  // retrasoFilter is client-side (dias_retraso is runtime-calculated, not stored)
  const filteredPolicies = retrasoFilter === 'all' ? policies : policies.filter(p => {
    const dias = p.dias_retraso_calculado ?? p.dias_retraso ?? 0;
    if (retrasoFilter === '0') return dias === 0;
    if (retrasoFilter === '1-30') return dias >= 1 && dias <= 30;
    if (retrasoFilter === '31-90') return dias >= 31 && dias <= 90;
    if (retrasoFilter === '90+') return dias > 90;
    return true;
  });

  const totalPremium = totalAcumulado;
  const activePolicies = activeCount ?? filteredPolicies.filter(p => p.status === 'active').length;

  // A4: datos del pie chart — 5 ramos fijos (Bug 1) + drill-down por ramo (Bug 3B)
  const ramoChartData = RAMOS_FIJOS.map(ramo => ({
    name: ramo,
    value: ramosData[ramo] || 0,
  }));
  const pieData = ramoSeleccionado
    ? distributionData
        .filter(item => item.ramo === ramoSeleccionado)
        .map(item => ({ name: item.producto, value: item.count }))
    : ramoChartData;

  const handleSort = (field) => {
    setSortDir(prev => sortBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortBy(field);
    setCurrentPage(1);
  };
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline-block flex-shrink-0" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 inline-block flex-shrink-0" />
      : <ArrowDown className="h-3 w-3 ml-1 inline-block flex-shrink-0" />;
  };


  const _hiddenInInversion = new Set(['days_overdue', 'chargeback']);
  const visiblePolicyCols = [...columns]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .filter(c => c.visible)
    .filter(c => activeGrupo !== 'inversion' || !_hiddenInInversion.has(c.id));

  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pólizas</h1>
          <p className="text-muted-foreground">Gestiona las pólizas de tus clientes</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <Button
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              data-testid="new-policy-btn"
              onClick={() => { setEditingPolicy(null); setDialogOpen(true); }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Póliza
            </Button>
          )}
          <PolicyFormModal
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setEditingPolicy(null);
            }}
            editingPolicy={editingPolicy}
            clients={clients}
            agents={agents}
            onSuccess={fetchData}
            policyCustomFields={policyCustomFields}
          />
          <ImportExportManager
            module="policies"
            apiUrl={API}
            data={policies}
            onSuccess={fetchData}
            fetchAllForExport={fetchAllPoliciesForExport}
            exportAsCsv={true}
            policyCustomFields={policyCustomFields}
          />
          {isMaster && (
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleReconcileRenewals}
              disabled={reconcilingRenewals}
              title="Revisar pólizas omitidas y marcar renovaciones"
            >
              <RefreshCw className={`h-4 w-4 ${reconcilingRenewals ? 'animate-spin' : ''}`} />
              Revisar renovaciones
            </Button>
          )}
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => navigate('/simulator')}
            data-testid="open-simulator-btn"
          >
            <Calculator className="h-4 w-4" />
            Simulador
          </Button>
        </div>
      </div>

      {/* ── Mi camino a la convención 2026 (solo tab Allianz) ───────────────── */}
      {activeAseguradora === 'allianz' && convPoints !== null && (
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setConvPanelOpen(o => !o)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 text-white">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    Mi camino a la convención 2026
                    {convPoints.estelar_qualified && (
                      <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs">
                        ✓ Estelar — Marruecos
                      </Badge>
                    )}
                    {!convPoints.estelar_qualified && convPoints.vip_qualified && (
                      <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 text-xs">
                        ✓ VIP — Huatulco
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Pólizas Allianz 2026 · {convPoints.policy_count ?? ''} pólizas calificadas
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isAdmin && agents.length > 0 && (
                  <div onClick={e => e.stopPropagation()}>
                    <Select
                      value={convAgentId || '__self__'}
                      onValueChange={v => setConvAgentId(v === '__self__' ? null : v)}
                    >
                      <SelectTrigger className="h-7 text-xs w-44">
                        <SelectValue placeholder="Mi cuenta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__self__">Mi cuenta</SelectItem>
                        {[...agents]
                          .sort((a, b) => a.full_name.localeCompare(b.full_name, 'es'))
                          .map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {convPoints.total_points} pts
                </span>
                {convPanelOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          </CardHeader>

          {convPanelOpen && (
            <CardContent className="space-y-4 pt-0">
              {/* Barras de progreso */}
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-blue-700 dark:text-blue-300">VIP — Huatulco</span>
                    <span className="text-muted-foreground">
                      {convPoints.total_points} / {CONV_VIP} pts
                      {convPoints.vip_qualified && <span className="ml-1 text-green-600 font-medium">✓</span>}
                    </span>
                  </div>
                  <Progress
                    value={Math.min((convPoints.total_points / CONV_VIP) * 100, 100)}
                    className="h-2"
                  />
                  {!convPoints.vip_qualified && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Faltan {Math.max(CONV_VIP - convPoints.total_points, 0).toFixed(1)} pts
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium text-amber-700 dark:text-amber-400">Estelar — Marruecos</span>
                    <span className="text-muted-foreground">
                      {convPoints.total_points} / {CONV_ESTELAR} pts
                      {convPoints.estelar_qualified && <span className="ml-1 text-green-600 font-medium">✓</span>}
                    </span>
                  </div>
                  <Progress
                    value={Math.min((convPoints.total_points / CONV_ESTELAR) * 100, 100)}
                    className="h-2 [&>div]:bg-amber-500"
                  />
                  {!convPoints.estelar_qualified && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Faltan {Math.max(CONV_ESTELAR - convPoints.total_points, 0).toFixed(1)} pts
                    </p>
                  )}
                </div>
              </div>

              {/* Desglose por categoría */}
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                {[
                  { key: 'ahorro',      label: 'Ahorro',      color: 'bg-sky-50 border-sky-200 text-sky-800 dark:bg-sky-900/20 dark:text-sky-300' },
                  { key: 'proteccion',  label: 'Protección',  color: 'bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' },
                  { key: 'gmmi',        label: 'GMMI',        color: 'bg-teal-50 border-teal-200 text-teal-800 dark:bg-teal-900/20 dark:text-teal-300' },
                  { key: 'autos',       label: 'Autos',       color: 'bg-orange-50 border-orange-200 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300' },
                  { key: 'residencial', label: 'Residencial', color: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:text-green-300' },
                  { key: 'inversion',   label: 'Inversión',   color: 'bg-indigo-50 border-indigo-200 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300' },
                  { key: 'danos',       label: 'Daños',       color: 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-900/20 dark:text-rose-300' },
                  { key: 'beneficios',  label: 'Beneficios',  color: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300' },
                ].map(({ key, label, color }) => (
                  <div key={key} className={`rounded-lg border p-2 text-center ${color}`}>
                    <div className="text-xs font-medium opacity-80">{label}</div>
                    <div className="text-lg font-bold">{convPoints.breakdown[key]}</div>
                    <div className="text-xs opacity-60">pts</div>
                  </div>
                ))}
              </div>

              {/* Indicadores Insignia / Quálitas */}
              {(convPoints.prima_insignia > 0 || convPoints.prima_qualitas > 0) && (
                <div className="flex flex-wrap gap-3 pt-1 border-t">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Info className="h-3 w-3" />
                    <span className="font-medium">Otras aseguradoras (reglas próximamente):</span>
                  </div>
                  {convPoints.prima_insignia > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300">
                      Insignia Life — Prima acumulada: ${convPoints.prima_insignia.toLocaleString('es-MX')}
                    </span>
                  )}
                  {convPoints.prima_qualitas > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300">
                      Quálitas — Prima acumulada: ${convPoints.prima_qualitas.toLocaleString('es-MX')}
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* ── Trazando tu camino — Insignia Life (solo tab Insignia) ─────────── */}
      {activeAseguradora === 'insignia' && convPoints !== null && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setConvPanelOpen(o => !o)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-600 to-purple-800 text-white">
                  <Trophy className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    Trazando tu camino — Insignia Life
                    {(convPoints.prima_insignia || 0) >= 2660000 && (
                      <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs">✈️ Lisboa</Badge>
                    )}
                    {(convPoints.prima_insignia || 0) >= 1150000 && (convPoints.prima_insignia || 0) < 2660000 && (
                      <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 text-xs">🏖️ Cancún</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Prima acumulada Insignia Life {new Date().getFullYear()}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ${(convPoints.prima_insignia || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </span>
                {convPanelOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          </CardHeader>
          {convPanelOpen && (
            <CardContent className="pt-0 space-y-3">
              <p className="text-xs text-muted-foreground">Prima computable acumulada</p>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">🏖️ Nacional — Cancún 2027</span>
                    <span className="text-muted-foreground">
                      ${(convPoints.prima_insignia || 0).toLocaleString('es-MX')} / $1,150,000
                    </span>
                  </div>
                  <Progress value={Math.min(100, ((convPoints.prima_insignia || 0) / 1150000) * 100)} className="h-2 [&>div]:bg-purple-500" />
                  {(convPoints.prima_insignia || 0) < 1150000 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Faltan ${Math.max(0, 1150000 - (convPoints.prima_insignia || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">✈️ Internacional — Lisboa 2027</span>
                    <span className="text-muted-foreground">
                      ${(convPoints.prima_insignia || 0).toLocaleString('es-MX')} / $2,660,000
                    </span>
                  </div>
                  <Progress value={Math.min(100, ((convPoints.prima_insignia || 0) / 2660000) * 100)} className="h-2 [&>div]:bg-purple-700" />
                  {(convPoints.prima_insignia || 0) < 2660000 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Faltan ${Math.max(0, 2660000 - (convPoints.prima_insignia || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Retail</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <div className="text-xl font-bold">{(activeCount + pendingCount + rehabilitatedCount) - inversionCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Inversión</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <div className="text-xl font-bold text-indigo-600">{inversionCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pólizas Activas</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <div className="text-xl font-bold text-green-600">{activePolicies}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pendientes de Pago</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <div className="text-xl font-bold text-amber-500">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pólizas Canceladas</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <div className="text-xl font-bold text-red-600">{cancelledCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pólizas Renovadas</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <div className="text-xl font-bold text-blue-600">{renewedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 pb-1">
            <div className="flex items-center justify-between gap-1">
              <CardTitle className="text-sm font-medium text-muted-foreground">Prima/Aport.</CardTitle>
              <select
                value={startYear === null ? 'all' : startYear}
                onChange={e => setStartYear(e.target.value === 'all' ? null : Number(e.target.value))}
                className="text-xs border rounded px-1 py-0.5 bg-background text-foreground cursor-pointer"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
                <option value="all">Todas</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="p-3 pt-1">
            <Accordion type="single" collapsible>
              <AccordionItem value="prima-aporte" className="border-none">
                <AccordionTrigger className="py-0 text-xs font-medium text-muted-foreground hover:no-underline">
                  Ver monto
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-1">
                  <div className="text-xl font-bold">{formatCurrency(totalPremium)}</div>
                  {startYear !== null && (
                    <p className="text-xs text-muted-foreground mt-0.5">Vigencia {startYear}</p>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>

      {/* ── Tabs por aseguradora ─────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b">
        {[
          { id: 'all',      label: 'Todas',        color: 'text-gray-700 border-gray-700' },
          { id: 'allianz',  label: 'Allianz',      color: 'text-blue-700 border-blue-700' },
          { id: 'insignia', label: 'Insignia Life', color: 'text-red-700 border-red-700' },
          { id: 'qualitas', label: 'Quálitas',      color: 'text-green-700 border-green-700' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabAseguradora(tab.id)}
            className={`px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
              activeAseguradora === tab.id
                ? `${tab.color} bg-transparent`
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Sub-tabs por grupo ────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-1">
        {[
          { id: 'todas',        label: 'Todas' },
          { id: 'activa',       label: 'Activas' },
          { id: 'por_renovar',  label: 'Por renovar' },
          { id: 'cancelada',    label: 'Canceladas' },
          { id: 'renovada',     label: 'Renovadas' },
          { id: 'rehabilitada', label: 'Rehabilitadas' },
          { id: 'rechazada',    label: 'Rechazadas' },
          ...(activeAseguradora === 'allianz' ? [{ id: 'inversion', label: 'Inversión' }] : []),
          { id: 'omitidas',     label: omittedCount > 0        ? `Omitidas (${omittedCount})`           : 'Omitidas' },
          { id: 'renovaciones', label: pendingRenewalCount > 0 ? `Renovaciones (${pendingRenewalCount})` : 'Renovaciones' },
          { id: 'renovadas_activas', label: renovadasActivasCount > 0 ? `Activas con Renovación (${renovadasActivasCount})` : 'Activas con Renovación' },
          { id: 'no_renovada', label: noRenovadasCount > 0 ? `No renovó (${noRenovadasCount})` : 'No renovó' },
        ].map(g => (
          <button
            key={g.id}
            onClick={() => handleTabGrupo(g.id)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeGrupo === g.id
                ? 'bg-[#1a365d] text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {g.label}
            {activeGrupo === g.id && totalRecords > 0 ? ` (${totalRecords})` : ''}
          </button>
        ))}
      </div>

      {/* ── Distribución por RAMO (pie chart) ──────────────────────────────────── */}
      {distributionData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Pie: ramos cuando no hay selección, productos cuando hay ramo activo */}
              <div className="w-[200px] h-[160px] flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      onClick={(entry) => {
                        if (!ramoSeleccionado) {
                          // Vista de ramos: entrar al drill-down del ramo
                          setRamoSeleccionado(entry.name);
                          setProductoFilter('');
                        } else {
                          // Vista de productos: filtrar tabla
                          setProductoFilter(prev => prev === entry.name ? '' : entry.name);
                        }
                        setCurrentPage(1);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {pieData.map((entry, i) => (
                        <Cell
                          key={entry.name}
                          fill={ramoSeleccionado
                            ? PIE_COLORS[i % PIE_COLORS.length]
                            : (RAMO_COLORS[entry.name] || '#94a3b8')}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(v, n) => {
                        const total = pieData.reduce((s, d) => s + d.value, 0);
                        return [`${v} (${total > 0 ? ((v / total) * 100).toFixed(0) : 0}%)`, n];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Leyenda: 5 ramos fijos (Bug 1) o productos del ramo (Bug 3B) */}
              <div className="flex flex-wrap gap-2 text-xs">
                {!ramoSeleccionado
                  ? RAMOS_FIJOS.map(ramo => (
                      <button
                        key={ramo}
                        onClick={() => { setRamoSeleccionado(ramo); setProductoFilter(''); setCurrentPage(1); }}
                        className="flex items-center gap-1 px-2 py-1 rounded-full border border-muted hover:border-muted-foreground transition-colors"
                      >
                        <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: RAMO_COLORS[ramo] }} />
                        {ramo} ({ramosData[ramo] || 0})
                      </button>
                    ))
                  : distributionData
                      .filter(d => d.ramo === ramoSeleccionado)
                      .map(d => (
                        <button
                          key={d.producto}
                          onClick={() => { setProductoFilter(prev => prev === d.producto ? '' : d.producto); setCurrentPage(1); }}
                          className={`flex items-center gap-1 px-2 py-1 rounded-full border transition-colors ${
                            productoFilter === d.producto
                              ? 'border-[#1a365d] bg-[#1a365d]/10 font-semibold'
                              : 'border-muted hover:border-muted-foreground'
                          }`}
                        >
                          <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: RAMO_COLORS[ramoSeleccionado] || '#94a3b8' }} />
                          {d.producto} ({d.count})
                        </button>
                      ))
                }
                {(ramoSeleccionado || productoFilter) && (
                  <button
                    onClick={() => { setRamoSeleccionado(null); setProductoFilter(''); setCurrentPage(1); }}
                    className="px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-300 text-xs"
                  >
                    Ver todos
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          {/* Row 1: search + movement + column customizer */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por número, cliente, agente o emisor..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-9"
                data-testid="search-policies"
              />
            </div>
            <Select value={movementFilter} onValueChange={(v) => { setMovementFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-[190px]">
                <RefreshCw className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Tipo movimiento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los movimientos</SelectItem>
                <SelectItem value="emision">Emisión</SelectItem>
                <SelectItem value="cancelacion">Cancelación</SelectItem>
                <SelectItem value="rehabilitacion">Rehabilitación</SelectItem>
              </SelectContent>
            </Select>
            <ColumnCustomizer
              columns={columns.filter(c => !c.id?.startsWith('cf_'))}
              storageKey="policies"
              onColumnsChange={setColumns}
              extraColumns={policyCustomFields}
              isAdmin={isAdmin}
            />
          </div>
          {/* Row 2: advanced filters */}
          <div className="flex flex-wrap gap-3">
            {(isAdmin || isMaster || isAsistente || isDeveloper) && (
              <Select value={agentFilter || 'all'} onValueChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <User className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user?.id}>Mis Pólizas</SelectItem>
                  {!isAsistente && <SelectItem value="all">Todos los agentes</SelectItem>}
                  {[...(isAsistente ? managedUsers : agents)]
                    .filter(a => a.id !== user?.id)
                    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es'))
                    .map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            )}
            {(isAdmin || isMaster || isAsistente) && (
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="active">Activas</SelectItem>
                  <SelectItem value="pending_payment">Pendiente de pago</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                </SelectContent>
              </Select>
            )}
            {(isAdmin || isMaster || isAsistente) && (
              <Select value={emisorFilter} onValueChange={(v) => { setEmisorFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[160px]">
                  <Building2 className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los emisores</SelectItem>
                  {emisorOptions.map(em => (
                    <SelectItem key={em} value={em}>{em}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center gap-1">
              <Select value={retrasoFilter} onValueChange={(v) => setRetrasoFilter(v)}>
                <SelectTrigger className="w-[170px]">
                  <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los retrasos</SelectItem>
                  <SelectItem value="0">Sin retraso (0 días)</SelectItem>
                  <SelectItem value="1-30">Leve (1–30 días)</SelectItem>
                  <SelectItem value="31-90">Moderado (31–90 días)</SelectItem>
                  <SelectItem value="90+">Crítico (+90 días)</SelectItem>
                </SelectContent>
              </Select>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help flex-shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>Filtra las pólizas visibles en esta página</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <Input
                type="date"
                value={startDateFrom}
                onChange={(e) => { setStartDateFrom(e.target.value); setCurrentPage(1); }}
                className="w-[140px] h-9 text-xs"
                title="Inicio vigencia desde"
              />
              <span className="text-muted-foreground text-xs">–</span>
              <Input
                type="date"
                value={startDateTo}
                onChange={(e) => { setStartDateTo(e.target.value); setCurrentPage(1); }}
                className="w-[140px] h-9 text-xs"
                title="Inicio vigencia hasta"
              />
              {(startDateFrom || startDateTo) && (
                <button
                  onClick={() => { setStartDateFrom(''); setStartDateTo(''); setCurrentPage(1); }}
                  className="text-muted-foreground hover:text-foreground"
                  title="Limpiar fechas"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table wrapperClassName="overflow-auto max-h-[calc(100vh-16rem)]">
              <TableHeader className="sticky top-0 z-20 bg-white dark:bg-gray-950 border-b shadow-sm">
                <TableRow>
                {visiblePolicyCols.map(col => {
                  if (col.id === 'policy_number') return (
                    <TableHead key="policy_number" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('policy_number')}>
                      <div className="flex items-center gap-1">Póliza <SortIcon field="policy_number" /></div>
                    </TableHead>
                  );
                  if (col.id === 'client') return (
                    <TableHead key="client" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('client_name')}>
                      <div className="flex items-center gap-1">Cliente <SortIcon field="client_name" /></div>
                    </TableHead>
                  );
                  if (col.id === 'agent') return (
                    <TableHead key="agent" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('agent_name')}>
                      <div className="flex items-center gap-1">Agente <SortIcon field="agent_name" /></div>
                    </TableHead>
                  );
                  if (col.id === 'business_line') return (
                    <TableHead key="business_line" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('business_line')}>
                      <div className="flex items-center gap-1">Aseguradora <SortIcon field="business_line" /></div>
                    </TableHead>
                  );
                  if (col.id === 'sede') return <TableHead key="sede">Sede</TableHead>;
                  if (col.id === 'product_type') return (
                    <TableHead key="product_type" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('product_type')}>
                      <div className="flex items-center gap-1">Producto <SortIcon field="product_type" /></div>
                    </TableHead>
                  );
                  if (col.id === 'premium') return <TableHead key="premium">Prima/Aportaciones</TableHead>;
                  if (col.id === 'vigencia') return (
                    <TableHead key="vigencia" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('start_date')}>
                      <div className="flex items-center gap-1">Vigencia <SortIcon field="start_date" /></div>
                    </TableHead>
                  );
                  if (col.id === 'movement_type') return <TableHead key="movement_type">Tipo movimiento</TableHead>;
                  if (col.id === 'status') return (
                    <TableHead key="status" className="cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">Estado <SortIcon field="status" /></div>
                    </TableHead>
                  );
                  if (col.id === 'days_overdue') return <TableHead key="days_overdue">Días retraso</TableHead>;
                  if (col.id === 'client_intention') return <TableHead key="client_intention">Intención cliente</TableHead>;
                  if (col.id === 'convention_pts') return (
                    <TableHead key="convention_pts" className="whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Trophy className="h-3 w-3 text-blue-600" />
                        Conv.
                      </div>
                    </TableHead>
                  );
                  if (col.id === 'chargeback') return <TableHead key="chargeback">Chargeback</TableHead>;
                  if (col.id === 'actions') return <TableHead key="actions" className="w-[50px]"></TableHead>;
                  if (col.id?.startsWith('cf_')) return (
                    <TableHead key={col.id} className="whitespace-nowrap">
                      {col.label}
                    </TableHead>
                  );
                  return null;
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visiblePolicyCols.length} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d] mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : filteredPolicies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visiblePolicyCols.length} className="text-center py-8 text-muted-foreground">
                    No se encontraron pólizas
                  </TableCell>
                </TableRow>
              ) : (
                filteredPolicies.map((policy) => (
                  <TableRow key={policy.id} data-testid={`policy-row-${policy.id}`}>
                    {visiblePolicyCols.map(col => {
                      if (col.id === 'policy_number') return (
                        <TableCell key="policy_number">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{policy.policy_number}</span>
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'client') return (
                        <TableCell key="client">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {policy.client_name}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'agent') return <TableCell key="agent">{policy.agent_name}</TableCell>;
                      if (col.id === 'business_line') return (
                        <TableCell key="business_line">
                          <Badge className={businessLineColors[policy.business_line]}>
                            {BUSINESS_LINES.find(l => l.id === policy.business_line)?.name || policy.business_line}
                          </Badge>
                        </TableCell>
                      );
                      if (col.id === 'sede') return (
                        <TableCell key="sede">
                          {policy.sede
                            ? <Badge className={`text-[10px] ${SEDE_COLORS_POL[policy.sede] || 'bg-gray-100 text-gray-700'}`}>{policy.sede}</Badge>
                            : <span className="text-muted-foreground text-xs">—</span>}
                        </TableCell>
                      );
                      if (col.id === 'product_type') return <TableCell key="product_type">{policy.product_type}</TableCell>;
                      if (col.id === 'premium') return (
                        <TableCell key="premium" className="font-medium">
                          {formatCurrency(policy.prima_anual_reportada ?? policy.premium ?? 0)}
                        </TableCell>
                      );
                      if (col.id === 'vigencia') return (
                        <TableCell key="vigencia">
                          <div className="text-sm">
                            <div>{formatDate(policy.start_date)}</div>
                            <div className="text-muted-foreground">al {formatDate(policy.end_date)}</div>
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'movement_type') return (
                        <TableCell key="movement_type">
                          <span className="text-sm text-muted-foreground">{policy.status === 'no_renewed' ? 'Cancelación' : (movementTypeLabels[policy.movement_type] || 'Emisión')}</span>
                        </TableCell>
                      );
                      if (col.id === 'status') return (
                        <TableCell key="status">
                          <div className="flex flex-col gap-1">
                            {policy.missing_reason === 'omitted' && policy.missing_reason_source === 'manual' ? (
                              <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                Omitida
                              </Badge>
                            ) : (
                              <>
                                <Badge className={policyStatuses[policy.status]}>
                                  {statusLabels[policy.status]}
                                </Badge>
                                {policy.missing_reason === 'omitted' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-orange-900 dark:text-orange-300 bg-gray-200 dark:bg-orange-900/30 border border-gray-400 dark:border-orange-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                                    🚫 Omitida
                                  </span>
                                )}
                              </>
                            )}
                            {policy.last_import_missing && policy.missing_reason !== 'omitted' && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                                ⚠ No en último import
                              </span>
                            )}
                            {policy.missing_reason === 'pending_renewal' && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                                🔄 Renovación pendiente
                              </span>
                            )}
                            {policy.movement_type === 'renovacion' && ['active', 'pending_payment', 'rehabilitated'].includes(policy.status) && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 border border-teal-300 dark:border-teal-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                                🔄 Renovada
                              </span>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'days_overdue') {
                        const color   = policy.dias_retraso_color;
                        const display = policy.dias_retraso_display;
                        if (!color || !display || display === '—') {
                          return (
                            <TableCell key="days_overdue">
                              <span className="text-muted-foreground text-sm">—</span>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key="days_overdue">
                            <Badge className={DAYS_OVERDUE_COLOR_CLASS[color] || 'bg-gray-100 text-gray-600'}>
                              {display}
                            </Badge>
                          </TableCell>
                        );
                      }
                      if (col.id === 'client_intention') return (
                        <TableCell key="client_intention">
                          {policy.client_intention && policy.client_intention !== 'no_definido' ? (
                            <Badge className={policy.client_intention === 'cancelar' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'}>
                              {intentionLabels[policy.client_intention]}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'convention_pts') {
                        const cp = calculatePolicyPoints(policy);
                        if (!cp || cp.nonQualifying) return <TableCell key="convention_pts"><span className="text-muted-foreground text-xs">—</span></TableCell>;
                        if (cp.pendiente) {
                          return (
                            <TableCell key="convention_pts">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-muted-foreground cursor-default underline decoration-dotted">
                                      ${(cp.prima || 0).toLocaleString('es-MX')}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs text-xs">
                                    <p className="font-medium">{cp.label}</p>
                                    <p className="text-muted-foreground">Prima acumulada — reglas próximamente</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key="convention_pts">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={`inline-flex items-center gap-1 text-xs font-semibold cursor-default px-1.5 py-0.5 rounded ${cp.pts > 0 ? 'text-blue-700 dark:text-blue-300' : 'text-muted-foreground'}`}>
                                    <Trophy className="h-3 w-3" />
                                    {cp.pts > 0 ? `${cp.pts} pt${cp.pts !== 1 ? 's' : ''}` : '0'}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-xs text-xs space-y-1">
                                  <p className="font-medium">{cp.label}</p>
                                  <p className="text-muted-foreground">{cp.basis}</p>
                                  {cp.note && <p className="text-amber-600 dark:text-amber-400">{cp.note}</p>}
                                  {policy.status === 'cancelled' && <p className="text-red-500">Póliza cancelada — no acumula puntos</p>}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        );
                      }
                      if (col.id === 'chargeback') return <TableCell key="chargeback">{renderChargeback(policy)}</TableCell>;
                      if (col.id && col.id.startsWith('cf_')) {
                        const fieldId = col.id.replace(/^cf_/, '');
                        const val = (policy.custom_values || {})[fieldId];
                        return (
                          <TableCell key={col.id}>
                            <span className="text-sm text-muted-foreground">{val || '—'}</span>
                          </TableCell>
                        );
                      }
                      if (col.id === 'actions') return (
                        <TableCell key="actions">
                          {(canEdit || policy.agent_id === user?.id) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(policy)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  setActualizarPagosModal(policy);
                                  setActualizarPagosInput(String(policy.numero_aportacion_actual || 0));
                                  setActualizarPagosDate('');
                                }}>
                                  <CreditCard className="mr-2 h-4 w-4" />
                                  Actualizar pagos
                                </DropdownMenuItem>
                                {!['cancelled', 'no_renewed'].includes(policy.status) && (
                                  <DropdownMenuItem onClick={() => {
                                    setCancelTarget(policy);
                                    setCancelDate(new Date().toISOString().split('T')[0]);
                                    setCancelDialogOpen(true);
                                  }} className="text-orange-600">
                                    <XCircle className="mr-2 h-4 w-4" />
                                    Cancelar póliza
                                  </DropdownMenuItem>
                                )}
                                {['cancelled', 'no_renewed'].includes(policy.status) && (
                                  <DropdownMenuItem onClick={() => {
                                    setRehabilitateTarget(policy);
                                    setRehabilitateDate(new Date().toISOString().split('T')[0]);
                                    setRehabilitateDialogOpen(true);
                                  }} className="text-green-600">
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Rehabilitar póliza
                                  </DropdownMenuItem>
                                )}
                                {policy.missing_reason !== 'omitted' && (
                                  <DropdownMenuItem
                                    onClick={() => handleMarkOmitted(policy)}
                                    className="text-gray-600"
                                  >
                                    <Ban className="mr-2 h-4 w-4" />
                                    Marcar como omitida
                                  </DropdownMenuItem>
                                )}
                                {(policy.status_history || []).length > 0 && (
                                  <DropdownMenuItem onClick={() => {
                                    setHistoryPolicy(policy);
                                    setHistoryDialogOpen(true);
                                  }}>
                                    <History className="mr-2 h-4 w-4" />
                                    Ver historial
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <DropdownMenuItem
                                    onClick={() => handleDelete(policy.id)}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Eliminar
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      );
                      return null;
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Paginación ────────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Página {currentPage} de {totalPages} &nbsp;·&nbsp; Total: {totalRecords} pólizas
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="gap-1"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Chargeback — Confirmar Pago Dialog */}
      {chargebackModal && (
        <Dialog open={!!chargebackModal} onOpenChange={() => setChargebackModal(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Confirmar pago de póliza
              </DialogTitle>
              <DialogDescription>
                Póliza: <strong>{chargebackModal.policy.policy_number}</strong>
                <br />
                Cliente: <strong>{chargebackModal.policy.client_name}</strong>
                <br />
                Pagos registrados: <strong>{chargebackModal.policy.serie_recibo_numerador ?? chargebackModal.policy.numero_aportacion_actual ?? 0}/{chargebackModal.policy.chargeback_serie_max ?? 18}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <p className="text-sm text-muted-foreground">
                ¿El cliente ya realizó su pago correspondiente?
                Al confirmar, se registrará 1 aportación adicional.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setChargebackModal(null)}>
                Cancelar
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleConfirmPago(chargebackModal.policy)}
              >
                ✓ Sí, ya pagó
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Actualizar Pagos Dialog */}
      {actualizarPagosModal && (
        <Dialog open={!!actualizarPagosModal} onOpenChange={closeActualizarPagos}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-blue-500" />
                Actualizar Pagos
              </DialogTitle>
              <DialogDescription>
                Póliza: <strong>{actualizarPagosModal.policy_number}</strong> — {actualizarPagosModal.client_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aportaciones registradas</span>
                  <span className="font-medium">{actualizarPagosModal.numero_aportacion_actual || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Días de atraso actuales</span>
                  <span className="font-medium">{actualizarPagosModal.dias_retraso_display || (actualizarPagosModal.dias_retraso > 0 ? `${actualizarPagosModal.dias_retraso} días` : 'Al corriente')}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Modo de ingreso</Label>
                <RadioGroup
                  value={modoIngreso}
                  onValueChange={v => { setModoIngreso(v); setActualizarPagosInput(''); setAportacionesIncrementales(''); }}
                  className="flex gap-6"
                >
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="total" id="modo-total" />
                    <Label htmlFor="modo-total" className="text-sm cursor-pointer font-normal">Total de aportaciones realizadas</Label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="incremental" id="modo-incremental" />
                    <Label htmlFor="modo-incremental" className="text-sm cursor-pointer font-normal">Agregar aportaciones faltantes</Label>
                  </div>
                </RadioGroup>
              </div>
              {modoIngreso === 'total' ? (
                <div className="space-y-2">
                  <Label htmlFor="ap-input">Aportaciones realizadas por el cliente (total)</Label>
                  <Input
                    id="ap-input"
                    type="number"
                    min={0}
                    value={actualizarPagosInput}
                    onChange={e => setActualizarPagosInput(e.target.value)}
                    placeholder="Ej. 12"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="ap-incr">Aportaciones a agregar</Label>
                  <Input
                    id="ap-incr"
                    type="number"
                    min={1}
                    value={aportacionesIncrementales}
                    onChange={e => setAportacionesIncrementales(e.target.value)}
                    placeholder="Ej. 3"
                  />
                  {(() => {
                    const n = parseInt(aportacionesIncrementales, 10);
                    const sr = actualizarPagosModal?.custom_values?.['9e4ab091-54de-4980-9eed-e6af30fbb3bf'];
                    if (!sr || isNaN(n) || n <= 0) return null;
                    const parts = sr.split('/');
                    if (parts.length !== 2) return null;
                    const x = parseInt(parts[0], 10);
                    if (isNaN(x)) return null;
                    return (
                      <p className="text-xs text-muted-foreground">
                        Serie Recibo: <span className="font-mono">{sr}</span>{' '}→{' '}
                        <span className="font-mono font-semibold text-foreground">{x + n}/{parts[1]}</span>
                      </p>
                    );
                  })()}
                </div>
              )}
              <div className="space-y-1">
                <Label htmlFor="ap-date">Fecha del último pago (opcional)</Label>
                <Input
                  id="ap-date"
                  type="date"
                  value={actualizarPagosDate}
                  onChange={e => setActualizarPagosDate(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">Se calcula automáticamente si se deja vacío</p>
              </div>
              {modoIngreso === 'total' && actualizarPagosInput !== '' && !isNaN(parseInt(actualizarPagosInput, 10)) && (
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 text-sm space-y-1 border border-blue-200 dark:border-blue-800">
                  <p className="font-semibold text-blue-800 dark:text-blue-300 mb-1">Vista previa</p>
                  {(() => {
                    const n = parseInt(actualizarPagosInput, 10);
                    const sr = actualizarPagosModal?.custom_values?.['9e4ab091-54de-4980-9eed-e6af30fbb3bf'];
                    if (sr) {
                      const parts = sr.split('/');
                      const y = parts.length === 2 ? parts[1] : '?';
                      return (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Serie Recibo</span>
                          <span className="font-mono font-medium">{sr} → {n}/{y}</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Chargeback</span>
                    <span className={parseInt(actualizarPagosInput, 10) >= 24 ? 'text-green-600 font-medium' : 'text-orange-600 font-medium'}>
                      {parseInt(actualizarPagosInput, 10) >= 24 ? 'No aplica' : 'Aplica'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estado resultante</span>
                    <span className="text-muted-foreground text-xs italic">Se mostrará al confirmar</span>
                  </div>
                </div>
              )}

              {/* ── Aportaciones adelantadas ── */}
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Aportaciones adelantadas</p>
                  <p className="text-xs text-muted-foreground">Registrar pago anticipado</p>
                </div>
                <Switch
                  checked={adelantadasActivo}
                  onCheckedChange={v => { setAdelantadasActivo(v); setAdelantadasTipo('cantidad'); setAdelantadasCantidad(''); setAdelantadasMonto(''); setSuspenderPagos(null); }}
                />
              </div>

              {adelantadasActivo && (
                <div className="space-y-3 rounded-lg border p-3 bg-muted/30">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Tipo de adelanto</Label>
                    <RadioGroup
                      value={adelantadasTipo}
                      onValueChange={v => { setAdelantadasTipo(v); setAdelantadasCantidad(''); setAdelantadasMonto(''); setSuspenderPagos(null); }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center gap-1.5">
                        <RadioGroupItem value="cantidad" id="adel-cantidad" />
                        <Label htmlFor="adel-cantidad" className="text-xs cursor-pointer">Por cantidad de períodos</Label>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <RadioGroupItem value="monto" id="adel-monto" />
                        <Label htmlFor="adel-monto" className="text-xs cursor-pointer">Por monto ($)</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {adelantadasTipo === 'cantidad' && (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Número de períodos adelantados</Label>
                        <Input
                          type="number"
                          min={1}
                          value={adelantadasCantidad}
                          onChange={e => setAdelantadasCantidad(e.target.value)}
                          placeholder="Ej. 3"
                          className="h-8 text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">¿Suspender cobros futuros?</Label>
                        <RadioGroup
                          value={suspenderPagos === null ? '' : String(suspenderPagos)}
                          onValueChange={v => setSuspenderPagos(v === 'true')}
                          className="flex gap-4"
                        >
                          <div className="flex items-center gap-1.5">
                            <RadioGroupItem value="true" id="susp-si" />
                            <Label htmlFor="susp-si" className="text-xs cursor-pointer">Sí — avanzar recibo</Label>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <RadioGroupItem value="false" id="susp-no" />
                            <Label htmlFor="susp-no" className="text-xs cursor-pointer">No — solo registrar</Label>
                          </div>
                        </RadioGroup>
                        <p className="text-xs text-muted-foreground">
                          "Sí" mueve la serie_recibo y la fecha del próximo recibo.<br />
                          "No" solo actualiza aportaciones sin cambiar el calendario.
                        </p>
                      </div>

                      {(() => {
                        const n = parseInt(adelantadasCantidad, 10);
                        const sr = actualizarPagosModal?.custom_values?.['9e4ab091-54de-4980-9eed-e6af30fbb3bf'];
                        const frp = actualizarPagosModal?.fecha_recibo_pendiente || actualizarPagosModal?.custom_values?.['6f0d045f-0329-4d21-898e-f97f299e475a'];
                        const per = actualizarPagosModal?.custom_values?.['05d71add-bc25-4479-9fc0-ddd36853b2cf'];
                        const newSerie = previewSerieRecibo(sr, n);
                        const newFecha = previewFechaRecibo(frp, n, per);
                        if (!newSerie && !newFecha) return null;
                        return (
                          <div className="bg-amber-50 dark:bg-amber-950/30 rounded p-2 text-xs space-y-1 border border-amber-200 dark:border-amber-800">
                            <p className="font-semibold text-amber-800 dark:text-amber-300">Vista previa del cambio</p>
                            {newSerie && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Serie recibo</span>
                                <span className="font-mono font-medium">{sr} → {newSerie}</span>
                              </div>
                            )}
                            {newFecha && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Próximo recibo</span>
                                <span className="font-mono font-medium">{frp} → {newFecha}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {adelantadasTipo === 'monto' && (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Monto adelantado ($)</Label>
                        <Input
                          type="number"
                          min={1}
                          value={adelantadasMonto}
                          onChange={e => setAdelantadasMonto(e.target.value)}
                          placeholder="Ej. 5000"
                          className="h-8 text-sm"
                        />
                      </div>
                      {(() => {
                        const m = parseFloat(adelantadasMonto);
                        const vpa = parseFloat(actualizarPagosModal?.custom_values?.['569aa9ab-8dd6-4e5b-94e8-0c4b32c45042'] || 0);
                        const vpo = parseFloat(actualizarPagosModal?.custom_values?.['74fe2d7f-0855-4f34-9f44-fb7e1ce5b472'] || 0);
                        const base = vpa || vpo;
                        if (isNaN(m) || m <= 0) return null;
                        return (
                          <div className="bg-green-50 dark:bg-green-950/30 rounded p-2 text-xs space-y-1 border border-green-200 dark:border-green-800">
                            <p className="font-semibold text-green-800 dark:text-green-300">Vista previa del abono</p>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Plan acumulado</span>
                              <span className="font-medium">${fmtMoney(base)} → ${fmtMoney(base + m)}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closeActualizarPagos}>
                Cancelar
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleActualizarPagos}
                disabled={actualizarPagosLoading || (
                  modoIngreso === 'incremental'
                    ? (aportacionesIncrementales === '' || isNaN(parseInt(aportacionesIncrementales, 10)) || parseInt(aportacionesIncrementales, 10) <= 0)
                    : (actualizarPagosInput === '' || isNaN(parseInt(actualizarPagosInput, 10)))
                )}
              >
                {actualizarPagosLoading ? 'Guardando...' : 'Confirmar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancelar póliza</DialogTitle>
            <DialogDescription>
              {cancelTarget?.policy_number} — {cancelTarget?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fecha de cancelación</Label>
              <Input type="date" value={cancelDate} onChange={e => setCancelDate(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Volver</Button>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleCancelPolicy}>
                <XCircle className="h-4 w-4 mr-2" />
                Confirmar cancelación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rehabilitate Dialog */}
      <Dialog open={rehabilitateDialogOpen} onOpenChange={setRehabilitateDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rehabilitar póliza</DialogTitle>
            <DialogDescription>
              {rehabilitateTarget?.policy_number} — {rehabilitateTarget?.client_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fecha de rehabilitación</Label>
              <Input type="date" value={rehabilitateDate} onChange={e => setRehabilitateDate(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRehabilitateDialogOpen(false)}>Volver</Button>
              <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleRehabilitatePolicy}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Confirmar rehabilitación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Historial de cambios</DialogTitle>
            <DialogDescription>{historyPolicy?.policy_number} — {historyPolicy?.client_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(historyPolicy?.status_history || []).map((h, i) => (
              <div key={i} className="flex items-center gap-3 text-sm border-b pb-2">
                <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <span className="font-medium">{movementTypeLabels[h.movement_type] || h.movement_type}</span>
                  <span className="text-muted-foreground"> — {h.from} → {h.to}</span>
                  <div className="text-xs text-muted-foreground">{h.date}</div>
                </div>
              </div>
            ))}
            {(historyPolicy?.status_history || []).length === 0 && (
              <p className="text-muted-foreground text-sm">Sin cambios registrados.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
